import type { components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type RoastBatchCost = components["schemas"]["RoastBatchCost"];
export type RoastBatchCostLine = components["schemas"]["RoastBatchCostLine"];

const BATCH_COST_PATH = "/api/v1/costing/batches/{batchId}/cost";

/**
 * What a roast cost (US-12.2).
 *
 * <p>Costing writes the figure just after the drop commits, so a card opened the instant the batch
 * was dropped can still get a 404. `retry` rides that out instead of showing "no cost" for something
 * that is on its way; `enabled` keeps the query away from batches that have not been dropped at all.
 */
export function useBatchCost(batchId: string, dropped: boolean) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    BATCH_COST_PATH,
    { params: { path: { batchId } } },
    { enabled: dropped, retry: 3, retryDelay: 1_000 },
  );
}

/** A drop correction moves the cost per kilogram of roasted coffee, so the card is refreshed. */
export function useInvalidateBatchCost() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["get", BATCH_COST_PATH] });
}
